import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, get, remove, onValue } from 'firebase/database';
import { getAuth } from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCAhCkPgbHNMkNj8epi6Nc_vX0AKaUdmXg",
  authDomain: "markersync-jamp.firebaseapp.com",
  databaseURL: "https://markersync-jamp-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "markersync-jamp",
  storageBucket: "markersync-jamp.firebasestorage.app",
  messagingSenderId: "322490956548",
  appId: "1:322490956548:web:8154dcec44f5bf7db6ee51"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getDatabase(app);

// LocalStorage key for backup key
const BACKUP_KEY_STORAGE = 'markerSync_backupKey';
const CLOUD_OPT_IN_KEY = 'markerSync_cloudOptIn';

// Fields allowed by Firebase rules for a marker in /collections
const ALLOWED_MARKER_FIELDS = new Set(['hex', 'brand', 'code', 'code_old', 'code_new', 'name', 'id']);

/**
 * Strip any fields from a marker object that aren't allowed by Firebase rules.
 * Prevents denies caused by extra fields (e.g. `sets`) present in source data.
 */
function sanitizeMarker(m) {
  const out = {};
  for (const key of ALLOWED_MARKER_FIELDS) {
    if (m[key] !== undefined) out[key] = m[key];
  }
  return out;
}

function sanitizeMarkers(markers) {
  return markers.map(sanitizeMarker);
}

/**
 * Generate a new backup key (UUID v4)
 * Note: Does NOT persist to localStorage until first successful save
 * This prevents orphaned keys from failed saves
 */
export function generateBackupKey() {
  let uuid;

  // Try crypto.randomUUID() first (modern browsers)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    uuid = crypto.randomUUID();
  } else {
    // Fallback for older browsers/mobile
    uuid = generateUUIDFallback();
  }

  const key = `usr_${uuid}`;
  return key;
}

/**
 * Fallback UUID v4 generator for browsers that don't support crypto.randomUUID()
 * @returns {string} UUID v4 string
 */
function generateUUIDFallback() {
  // Use current timestamp and random numbers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get existing backup key from localStorage
 * Returns null if user hasn't enabled cloud backup yet
 */
export function getBackupKey() {
  return localStorage.getItem(BACKUP_KEY_STORAGE);
}

/**
 * Check if user has cloud backup enabled
 */
export function hasCloudBackup() {
  const backupKey = getBackupKey();
  if (!backupKey) return false;

  const optIn = localStorage.getItem(CLOUD_OPT_IN_KEY);

  // Migration: if user already had a backup key, assume opt-in and persist
  if (optIn === null) {
    localStorage.setItem(CLOUD_OPT_IN_KEY, 'true');
    return true;
  }

  return optIn === 'true';
}

/**
 * Import backup key from user input (from another device)
 * Supports both formatted (MKSYNC-XXXXXXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX) and raw (usr_...) formats
 * Case-insensitive for formatted keys
 */
export function importBackupKey(keyString) {
  const cleaned = keyString.trim();
  const upperCleaned = cleaned.toUpperCase();

  // Handle formatted key (MKSYNC-XXXXXXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX) - case insensitive
  if (upperCleaned.startsWith('MKSYNC-')) {
    const raw = parseBackupKey(upperCleaned);
    localStorage.setItem(BACKUP_KEY_STORAGE, raw);
    localStorage.setItem(CLOUD_OPT_IN_KEY, 'true');
    return raw;
  }
  // Handle raw UUID format (usr_...)
  else if (cleaned.startsWith('usr_')) {
    // Validate UUID format
    const uuidPart = cleaned.substring(4);
    if (!isValidUUID(uuidPart)) {
      throw new Error('Invalid UUID format in backup key');
    }
    localStorage.setItem(BACKUP_KEY_STORAGE, cleaned);
    localStorage.setItem(CLOUD_OPT_IN_KEY, 'true');
    return cleaned;
  }
  else {
    throw new Error('Invalid backup key format. Expected MKSYNC-XXXXXXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX or usr_...');
  }
}

/**
 * Validate UUID format (with or without dashes)
 */
function isValidUUID(uuid) {
  const uuidRegex = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Format backup key for display to user
 * Converts: usr_550e8400-e29b-41d4-a716-446655440000
 * To:       MKSYNC-550E8400-E29B-41D4-A716-4466-5544-0000
 *
 * This is a LOSSLESS conversion - we can perfectly reconstruct the original UUID
 */
export function formatBackupKey(uuid) {
  if (!uuid) return '';

  // Validate input
  if (!uuid.startsWith('usr_')) {
    throw new Error('Invalid backup key: must start with usr_');
  }

  // Remove 'usr_' prefix and all dashes to get 32 hex chars
  const cleaned = uuid.replace('usr_', '').replace(/-/g, '');

  // Validate we have exactly 32 hex characters
  if (cleaned.length !== 32 || !/^[0-9a-f]{32}$/i.test(cleaned)) {
    throw new Error('Invalid backup key: expected 32 hex characters');
  }

  // Uppercase for display
  const upper = cleaned.toUpperCase();

  // Format as MKSYNC-XXXXXXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX (8-4-4-4-4-4-4)
  return `MKSYNC-${upper.substring(0, 8)}-${upper.substring(8, 12)}-${upper.substring(12, 16)}-${upper.substring(16, 20)}-${upper.substring(20, 24)}-${upper.substring(24, 28)}-${upper.substring(28, 32)}`;
}

/**
 * Parse formatted backup key back to raw UUID
 * Converts: MKSYNC-550E8400-E29B-41D4-A716-4466-5544-0000
 * To:       usr_550e8400-e29b-41d4-a716-446655440000
 *
 * This perfectly reconstructs the original UUID (lossless)
 */
function parseBackupKey(formatted) {
  // Remove 'MKSYNC-' prefix and all dashes
  const cleaned = formatted.replace('MKSYNC-', '').replace(/-/g, '').toLowerCase();

  // Validate we have exactly 32 hex characters
  if (cleaned.length !== 32 || !/^[0-9a-f]{32}$/i.test(cleaned)) {
    throw new Error('Invalid formatted backup key: expected 32 hex characters after MKSYNC-');
  }

  // Reconstruct UUID format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
  const uuid = `${cleaned.substring(0, 8)}-${cleaned.substring(8, 12)}-${cleaned.substring(12, 16)}-${cleaned.substring(16, 20)}-${cleaned.substring(20, 32)}`;

  // Return as usr_ prefixed key
  return `usr_${uuid}`;
}

/**
 * Save collection to Firebase cloud
 * Only persists backup key to localStorage AFTER successful save (prevents orphaned keys)
 *
 * @param {Array} markers - Array of marker objects
 * @returns {Object} - {success: boolean, backupKey: string, isNewUser: boolean, error?: string}
 */
export async function saveToCloud(markers) {
  let backupKey = getBackupKey();
  const isNewUser = !backupKey;

  // Generate backup key if this is first time (but don't persist yet)
  if (!backupKey) {
    backupKey = generateBackupKey();
  }

  try {
    // Save to Firebase
    // Note: Firebase converts empty arrays to null, so we explicitly use an empty object
    // with markers set to an empty array indicator
    const clean = sanitizeMarkers(markers);
    await set(ref(db, `collections/${backupKey}`), {
      markers: clean.length === 0 ? null : clean, // Firebase stores empty array as null
      updatedAt: new Date().toISOString(),
      markerCount: clean.length,
      version: 1, // For future schema migrations
      isEmpty: clean.length === 0 // Explicit flag for empty collection
    });

    // Only persist backup key AFTER successful save
    if (isNewUser) {
      localStorage.setItem(BACKUP_KEY_STORAGE, backupKey);
    }
    localStorage.setItem(CLOUD_OPT_IN_KEY, 'true');

    return {
      success: true,
      backupKey,
      isNewUser // True if this was first cloud save
    };
  } catch (error) {
    console.error('Failed to save to cloud:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Load collection from Firebase cloud
 *
 * @param {string} backupKey - Optional backup key (if restoring from another device)
 * @returns {Object} - {success: boolean, markers: Array, updatedAt: string, error?: string}
 */
export async function loadFromCloud(backupKey = null) {
  // Use provided key or get from localStorage
  const key = backupKey || getBackupKey();

  if (!key) {
    return {
      success: false,
      error: 'No backup key found. Enable cloud backup first.'
    };
  }

  try {
    const snapshot = await get(ref(db, `collections/${key}`));

    if (snapshot.exists()) {
      const data = snapshot.val();

      // If restoring with a backup key (new device), save it to localStorage
      if (backupKey && backupKey !== getBackupKey()) {
        localStorage.setItem(BACKUP_KEY_STORAGE, backupKey);
        localStorage.setItem(CLOUD_OPT_IN_KEY, 'true');
      }

      // Check if collection is explicitly empty
      if (data.isEmpty || data.markerCount === 0) {
        return {
          success: true,
          markers: [],
          updatedAt: data.updatedAt,
          markerCount: 0
        };
      }

      // Convert markers to array if Firebase stored it as object
      let markers = data.markers || [];
      if (!Array.isArray(markers) && typeof markers === 'object') {
        // Firebase sometimes stores arrays as objects with numeric keys
        markers = Object.values(markers);
      }

      return {
        success: true,
        markers: markers,
        updatedAt: data.updatedAt,
        markerCount: data.markerCount
      };
    } else {
      // No data found - return empty collection
      return {
        success: true,
        markers: []
      };
    }
  } catch (error) {
    console.error('Failed to load from cloud:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Disconnect from cloud backup (remove local key only, don't delete cloud data)
 * Use this to start a fresh collection on a different device
 */
export function disconnectFromCloud() {
  const backupKey = getBackupKey();

  if (!backupKey) {
    return {
      success: false,
      error: 'No cloud backup to disconnect from'
    };
  }

  try {
    // Remove backup key from localStorage only
    // The cloud data remains intact for other devices
    localStorage.removeItem(BACKUP_KEY_STORAGE);
    localStorage.removeItem(CLOUD_OPT_IN_KEY);

    return { success: true };
  } catch (error) {
    console.error('Failed to disconnect from cloud:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Delete collection from Firebase cloud
 * Also removes backup key from localStorage
 */
export async function deleteFromCloud() {
  const backupKey = getBackupKey();

  if (!backupKey) {
    return {
      success: false,
      error: 'No cloud backup to delete'
    };
  }

  try {
    await remove(ref(db, `collections/${backupKey}`));

    // Remove backup key from localStorage
    localStorage.removeItem(BACKUP_KEY_STORAGE);
    localStorage.removeItem(CLOUD_OPT_IN_KEY);

    return { success: true };
  } catch (error) {
    console.error('Failed to delete from cloud:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Subscribe to real-time updates from Firebase
 * Automatically updates when collection changes on another device
 *
 * @param {Function} callback - Called with {markers, updatedAt} when data changes
 * @returns {Function} - Unsubscribe function (call to stop listening)
 */
export function subscribeToCollectionUpdates(callback) {
  const backupKey = getBackupKey();

  if (!backupKey) {
    console.warn('No backup key found - cannot subscribe to updates');
    return () => {}; // Return no-op unsubscribe
  }

  const collectionRef = ref(db, `collections/${backupKey}`);

  // Listen for changes
  const unsubscribe = onValue(collectionRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();

      // Convert markers to array if needed
      let markers = data.markers || [];
      if (!Array.isArray(markers) && typeof markers === 'object') {
        markers = Object.values(markers);
      }

      callback({
        markers: markers,
        updatedAt: data.updatedAt
      });
    } else {
      callback({
        markers: [],
        updatedAt: null
      });
    }
  }, (error) => {
    console.error('Error subscribing to updates:', error);
  });

  return unsubscribe;
}

/**
 * Download backup key as a text file
 * User can save this file and import it on another device
 */
export function downloadBackupKeyFile() {
  const backupKey = getBackupKey();

  if (!backupKey) {
    throw new Error('No backup key to download. Enable cloud backup first.');
  }

  const formatted = formatBackupKey(backupKey);
  const content = `MarkerSync Backup Key

Your backup key: ${formatted}

Keep this safe! You'll need it to restore your marker collection on other devices.

How to use:
1. On a new device, visit https://markersync.com
2. Click on "Collection" > "Manage" > "Restore from Cloud"
3. Enter this backup key

Last saved: ${new Date().toLocaleString()}

---
MarkerSync - Professional Marker Matching
https://markersync.com
`;

  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `markersync-backup-key-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();

  // Cleanup after a short delay to ensure download started
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Import backup key from uploaded text file
 *
 * @param {File} file - Text file containing backup key
 * @returns {Promise<string>} - The imported backup key
 */
export function importBackupKeyFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;

        // Extract backup key from file content
        // Look for MKSYNC-XXXXXXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX pattern (case insensitive)
        const match = content.match(/MKSYNC-[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}/i);

        if (match) {
          const backupKey = importBackupKey(match[0]);
          resolve(backupKey);
        } else {
          // Try to find usr_ format as fallback
          const rawMatch = content.match(/usr_[a-f0-9-]+/i);
          if (rawMatch) {
            const backupKey = importBackupKey(rawMatch[0]);
            resolve(backupKey);
          } else {
            reject(new Error('No valid backup key found in file'));
          }
        }
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}

/**
 * Copy formatted backup key to clipboard
 */
export async function copyBackupKeyToClipboard() {
  const backupKey = getBackupKey();

  if (!backupKey) {
    throw new Error('No backup key to copy. Enable cloud backup first.');
  }

  const formatted = formatBackupKey(backupKey);

  try {
    await navigator.clipboard.writeText(formatted);
    return formatted;
  } catch (error) {
    // Fallback for browsers that don't support clipboard API
    const textArea = document.createElement('textarea');
    textArea.value = formatted;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return formatted;
    } catch (err) {
      document.body.removeChild(textArea);
      throw new Error('Failed to copy to clipboard');
    }
  }
}

// ============================================
// Google Account Cloud Functions
// ============================================

/**
 * Save collection to Firebase using Google Auth UID
 * Used when user is logged in with Google account
 *
 * @param {Array} markers - Array of marker objects
 * @param {string} userId - Google Auth UID
 * @param {Object} options - Optional settings { migratedFrom: 'usr_xxx' }
 * @returns {Object} - {success: boolean, error?: string}
 */
export async function saveToCloudWithGoogle(markers, userId, options = {}) {
  if (!userId) {
    return { success: false, error: 'No user ID provided' };
  }

  try {
    // Check if collection exists to preserve createdAt
    const collectionRef = ref(db, `collections/${userId}`);
    const snapshot = await get(collectionRef);
    const existingData = snapshot.val();

    const clean = sanitizeMarkers(markers);
    const data = {
      markers: clean.length === 0 ? null : clean,
      createdAt: existingData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      markerCount: clean.length,
      version: 1,
      isEmpty: clean.length === 0
    };

    // Add migration tracking if provided
    if (options.migratedFrom) {
      data.migratedFrom = options.migratedFrom;
    }

    await set(collectionRef, data);

    // Update local timestamp
    localStorage.setItem('markerCollectionTimestamp', Date.now().toString());

    return { success: true };
  } catch (error) {
    console.error('Failed to save to cloud with Google:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Load collection from Firebase using Google Auth UID
 *
 * @param {string} userId - Google Auth UID
 * @returns {Object} - {success: boolean, markers: Array, updatedAt: string, error?: string}
 */
export async function loadFromCloudWithGoogle(userId) {
  if (!userId) {
    return { success: false, error: 'No user ID provided' };
  }

  try {
    const snapshot = await get(ref(db, `collections/${userId}`));

    if (snapshot.exists()) {
      const data = snapshot.val();

      // Check if collection is explicitly empty
      if (data.isEmpty || data.markerCount === 0) {
        return {
          success: true,
          markers: [],
          updatedAt: data.updatedAt,
          createdAt: data.createdAt,
          markerCount: 0
        };
      }

      // Convert markers to array if Firebase stored it as object
      let markers = data.markers || [];
      if (!Array.isArray(markers) && typeof markers === 'object') {
        markers = Object.values(markers);
      }

      return {
        success: true,
        markers: markers,
        updatedAt: data.updatedAt,
        createdAt: data.createdAt,
        markerCount: data.markerCount
      };
    } else {
      // No collection exists yet
      return {
        success: true,
        markers: [],
        updatedAt: null,
        createdAt: null,
        markerCount: 0
      };
    }
  } catch (error) {
    console.error('Failed to load from cloud with Google:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete collection from Firebase using Google Auth UID
 * Also deletes user record from /users/
 *
 * @param {string} userId - Google Auth UID
 * @returns {Object} - {success: boolean, error?: string}
 */
export async function deleteGoogleUserData(userId) {
  if (!userId) {
    return { success: false, error: 'No user ID provided' };
  }

  try {
    // Delete collection
    await remove(ref(db, `collections/${userId}`));

    // Delete user record
    await remove(ref(db, `users/${userId}`));

    return { success: true };
  } catch (error) {
    console.error('Failed to delete Google user data:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get current Firebase Auth instance
 * Useful for checking auth state outside of React context
 */
export function getFirebaseAuth() {
  return getAuth(app);
}
